// Shared init — runs whether content is inside a drawer or inline on the page.
function initGiftCardBalanceChecker() {
  const SERVER_ERROR_CODES = [400, 401, 403, 404, 405, 408, 409, 410, 422, 429, 500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511];

  // Read image URLs passed from Liquid via data attributes
  const sectionEl = document.querySelector('[data-result-image]');
  const resultImageUrl = sectionEl?.dataset.resultImage || '';
  const loaderImageUrl = sectionEl?.dataset.loaderImage || '';
  const sectionElement = document.querySelector('[data-cardigan-endpoint]');
  const spaElement = document.querySelector('[data-spa-endpoint]');

  // PAGE MODE ONLY — everything gated behind this flag leaves drawer mode untouched.
  const isPageMode = sectionEl?.dataset.displayMode === 'page';

  const input = document.querySelector('[data-cardigan-balance-checker="number"]');
  const form = document.querySelector('[data-cardigan-balance-checker="form"]');
  const submitBtn = document.querySelector('[data-cardigan-balance-checker="submit"]');
  if (submitBtn) {
    submitBtn.textContent = 'check balance & expiry';
  }

  // --- Page-mode only helpers: expand animation + "check another gift card" relocation ---

  // How far below the (possibly sticky) header the section top should sit
  // when we lock the viewport during a recheck.
  const HEADER_OFFSET = 85;

  // Height of any sticky/fixed header, so we don't scroll the section
  // underneath it. Adjust the selector list if your theme uses a
  // different sticky header wrapper.
  const getHeaderHeight = () => {
    const headerEl = document.querySelector('sticky-header, .shopify-section-header, .header-wrapper, header');
    if (!headerEl) return 0;
    const style = getComputedStyle(headerEl);
    return (style.position === 'sticky' || style.position === 'fixed')
      ? headerEl.getBoundingClientRect().height
      : 0;
  };

  // Locks the viewport to the top of this gift-card section (offset below
  // the header). Called right before a recheck collapses the result down
  // to the loader — without this, the section shrinks, the scroll
  // position stays the same, and the user ends up looking at whatever
  // section comes next instead of the loader/new result.
  const scrollSectionIntoView = () => {
    if (!isPageMode) return;
    const anchorEl = document.querySelector('.endota-gift-card-section');
    if (!anchorEl) return;
    const headerHeight = getHeaderHeight();
    const top = anchorEl.getBoundingClientRect().top + window.pageYOffset - headerHeight - HEADER_OFFSET;
    window.scrollTo({ top, behavior: 'auto' }); // instant — avoids fighting the shrink/expand animation
  };

  // Resolves once every <img> inside the container has either loaded or errored,
  // so we never measure scrollHeight while an image still has 0 height.
  const waitForImages = (el) => {
    const imgs = Array.from(el.querySelectorAll('img'));
    return Promise.all(
      imgs.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      })
    );
  };

  const expandResultAnimation = (el) => {
    if (!isPageMode || !el) return;

    // Make the result visible
    el.style.display = 'block';

    // Reset previous animation
    el.classList.remove('is-expanded');
    el.style.maxHeight = '0px';

    // Force reflow
    void el.offsetHeight;

    // Wait until images (e.g. the gift-card background image) have actually
    // loaded before measuring — otherwise scrollHeight is captured too small
    // on the first render and gets clipped once the image paints in.
    waitForImages(el).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const height = el.scrollHeight;
          el.style.maxHeight = `${height}px`;
          el.classList.add('is-expanded');

          // Extra safety net: once the expand transition finishes, release the
          // fixed max-height entirely so any later layout shift (font swap,
          // table re-render, etc.) never gets clipped again either.
          el.addEventListener(
            'transitionend',
            function onEnd(e) {
              if (e.propertyName === 'max-height') {
                el.style.maxHeight = 'none';
                el.removeEventListener('transitionend', onEnd);
              }
            }
          );
        });
      });
    });
  };

  const moveToRecheckState = () => {
    if (!isPageMode) return;
    const formWrap = document.querySelector('.gift-card-form-wrap');
    const resultSection = document.querySelector('.gift-card-result');
    const formHeading = document.querySelector('.gift-card-form-heading');
    if (!formWrap || !resultSection) return;

    if (formHeading) {
      formHeading.textContent = 'check another gift card';
      formHeading.style.display = 'block';
    }

    // Relocate the existing form directly beneath the rendered result
    // so the next submission visually replaces the current card.
    resultSection.insertAdjacentElement('afterend', formWrap);
  };

  if (input && form) {
    input.removeAttribute('required');
    input.setAttribute('placeholder', 'Enter your gift card number');

    const errorMsg = document.createElement('div');
    errorMsg.className = 'form-message form-message--error';
    errorMsg.style.display = 'none';
    input.insertAdjacentElement('afterend', errorMsg);

    const showError = (message) => {
      errorMsg.textContent = message;
      errorMsg.style.display = 'block';
      input.classList.add('input--error');
    };

    const clearError = () => {
      errorMsg.textContent = '';
      errorMsg.style.display = 'none';
      input.classList.remove('input--error');
    };

    const handleServerError = () => {
      document.querySelector('.gift-card-loading')?.remove();

      const validTarget = document.querySelector('.gift-card-result_valid');
      const invalidTarget = document.querySelector('.gift-card-result_invalid');

      // No card shell on error — just the invalid/error message. An empty
      // card container here was leaving blank space above the message.
      if (validTarget) {
        validTarget.innerHTML = '';
        validTarget.style.display = 'none';
      }
      if (invalidTarget) { invalidTarget.style.display = 'block'; }

      const btn = document.querySelector('[data-cardigan-balance-checker="submit"]');
      input.disabled = false;
      input.removeAttribute('disabled');
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.classList.remove('cardigan-button--loading');
      }

      showError('We are currently unable to check the balance of your gift card, please check back later.');

      // Page mode: still animate + surface the recheck state on error
      requestAnimationFrame(() => {
        expandResultAnimation(document.querySelector('.gift-card-result'));
      });
      moveToRecheckState();
    };

    window.handleGiftCardServerError = handleServerError;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const value = input.value.trim().replace(/\s+/g, '');

      if (!value) { showError('This field is required'); return; }
      if (!/^\d{20}$/.test(value)) { showError('Invalid card number'); return; }

      clearError();

      const resultSection = document.querySelector('.gift-card-result');

      // Page mode recheck: lock the viewport to the top of this section
      // *before* the result collapses down to the loader. Without this,
      // the result shrinks (card -> loader), the page gets shorter above
      // the same scroll position, and the user ends up looking at the
      // next section instead of the loader/new result.
      if (isPageMode && resultSection && resultSection.classList.contains('is-expanded')) {
        scrollSectionIntoView();
      }

      // Hide guide, show result container — identical in both modes
      const detailsEl = document.querySelector('.gift-card-details');
      if (detailsEl) detailsEl.style.display = 'none';
      if (resultSection && !isPageMode) resultSection.style.display = 'block';

      const validTarget = document.querySelector('.gift-card-result_valid');
      const invalidTarget = document.querySelector('.gift-card-result_invalid');
      if (validTarget)   validTarget.style.display   = 'none';
      if (invalidTarget) invalidTarget.style.display = 'none';

      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'gift-card-loading text-center';
      const loaderImg = document.createElement('img');
      loaderImg.src = loaderImageUrl;
      loaderImg.alt = 'Loading...';
      loaderImg.style.width = '40px';
      loaderImg.style.height = '40px';
      loadingDiv.appendChild(loaderImg);
      resultSection?.appendChild(loadingDiv);

      fetch(form.action, { method: form.method || 'POST', body: new FormData(form) })
        .catch((err) => { console.error('Error:', err); });
    });

    input.addEventListener('input', function () {
      const value = input.value.trim().replace(/\s+/g, '');
      if (value && !/^\d{20}$/.test(value)) showError('Invalid card number');
      else clearError();
    });
  }

  // --- Fetch Interceptor ---
  const API_PREFIX    = sectionElement?.dataset.cardiganEndpoint || '';
  const SPA_ENDPOINT = spaElement?.dataset.spaEndpoint || '';
  const originalFetch = window.fetch;

  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(dateString));
  };

  // FIX: Fetch display names for all unique transaction references before rendering
  const fetchDisplayNames = async (transactions) => {
    const spaDisplayNameMap = {};

    const uniqueCodes = [...new Set(
      transactions
        .map(tx => tx.reference)
        .filter(ref => ref && ref !== 'WEB' && ref !== 'DEF')
    )];

    await Promise.all(
      uniqueCodes.map(code =>
        originalFetch(`${SPA_ENDPOINT}/api/location/display-name?code=${code}`)
          .then(res => res.json())
          .then(data => {
            if (data?.success) {
              spaDisplayNameMap[code] = data.displayName;
            }
          })
          .catch(err => console.error('Failed to fetch display name for code:', code, err))
      )
    );

    return spaDisplayNameMap;
  };

  const renderCard = async ({ number = '', expire = '', balance = '', transactions = [], isValid = false, expiresAt = '' }) => {
    const isExpired = isValid && expiresAt ? new Date(expiresAt) < new Date() : false;
    const iconHTML = document.querySelector('.gift-card-expired-icon')?.innerHTML || '';
    let transactionsHTML = '';

    if (isValid) {
      // FIX: Await all display names before building the HTML
      const spaDisplayNameMap = await fetchDisplayNames(transactions);
      transactionsHTML = `
        <div class="gift-card-transactions">
          <h3>Transaction History</h3>
          <table class="transaction-table">
            <thead>
              <tr><th>Date</th><th>Description</th><th>Amount</th></tr>
            </thead>
            <tbody>
              ${transactions.length > 0
                ? transactions.map((tx) => {
                  // FIX: displayText computed outside the template literal
                  let displayText = '';
                  if (tx.reference === 'WEB' || tx.reference === 'DEF') {
                    displayText = 'Online';
                  } else {
                    displayText = spaDisplayNameMap[tx.reference] || tx.reference || '';
                  }
                  return `
                    <tr>
                      <td>${formatFullDate(tx.timestamp_formatted)}</td>
                      <td>${displayText}</td>
                      <td>${tx.amount_formatted || ''}</td>
                    </tr>
                  `;
                  }).join('') + `
                    <tr class="current-balance">
                      <td></td>
                      <td style="font-weight:bold;">Current Balance</td>
                      <td style="font-weight:bold;">${balance}</td>
                    </tr>`
                : `<tr><td colspan="3" style="text-align:center;color:#666;font-style:italic;">This card has no transactions</td></tr>`
              }
            </tbody>
          </table>
        </div>`;
    }

    // resultImageUrl is read from the section data attribute
    // TODO: confirm this points at the same background asset used by the
    // My Account / Gifting Page balance checkers (swap the section image
    // setting if that asset is a different, shared file).
    const imageHTML = resultImageUrl
      ? `<img src="${resultImageUrl}" alt="Gift card result" class="gift-sample-result">`
      : '';

    return `
      <div class="gift-card-image">
        ${isExpired ? `<div class="gift-card-expired-badge">${iconHTML} Gift Card has expired</div>` : ''}
        ${imageHTML}
        <div class="gift-card-details">
          <div class="gift-card-number">${number}</div>
          <div class="gift-card-expire_date">EXPIRY<br>DATE <strong>${expire}</strong></div>
          <div class="gift-card-balance">BALANCE <strong>${balance}</strong></div>
        </div>
      </div>
      ${transactionsHTML}`;
  };

  // Guard: never double-patch fetch
  if (window.__giftCardFetchPatched) return;
  window.__giftCardFetchPatched = true;

  window.fetch = async function (...args) {
    let [resource] = args;

    if (typeof resource === 'string' && resource.startsWith(API_PREFIX)) {
      resource = resource.replace(/\/cards\/([^/?]+)\.json/, (match, cardNumber) => {
        const cleanNumber = decodeURIComponent(cardNumber).replace(/\s+/g, '');
        return `/cards/${cleanNumber}.json`;
      });
      resource += (resource.includes('?') ? '&' : '?') + 'include_transactions=true';
      args[0] = resource;
    }

    try {
      const response = await originalFetch(...args);

      if (typeof resource === 'string' && resource.startsWith(API_PREFIX)) {
        if (SERVER_ERROR_CODES.includes(response.status)) {
          window.handleGiftCardServerError?.();
          return response;
        }

        response.clone().json().then(async (data) => {
          const validTarget = document.querySelector('.gift-card-result_valid');
          const invalidTarget = document.querySelector('.gift-card-result_invalid');
          document.querySelector('.gift-card-loading')?.remove();

          if (!validTarget || !invalidTarget) return;

          const card = data.card || {};
          const transactions = data.transactions || [];
          const inputEl = document.querySelector('[data-cardigan-balance-checker="number"]');
          const userInput = inputEl ? inputEl.value.trim().replace(/\s+/g, '') : '';
          const isValid = card.last_characters && userInput.endsWith(card.last_characters);

          validTarget.innerHTML = isValid
            ? await renderCard({
                number: userInput,
                expire: formatDate(card.expires_at),
                balance: card.balance_formatted,
                transactions,
                isValid: true,
                expiresAt: card.expires_at,
              })
            : '';

          // Only show the card container when there's an actual card to show —
          // otherwise it leaves empty space above the invalid-card message.
          validTarget.style.display = isValid ? 'block' : 'none';
          invalidTarget.style.display = isValid ? 'none' : 'block';

          // Page mode: expand the card in and surface the recheck prompt
          requestAnimationFrame(() => {
            expandResultAnimation(document.querySelector('.gift-card-result'));
          });
          moveToRecheckState();
        }).catch(() => window.handleGiftCardServerError?.());
      }

      return response;
    } catch (error) {
      if (typeof resource === 'string' && resource.startsWith(API_PREFIX)) {
        window.handleGiftCardServerError?.();
      }
      throw error;
    }
  };
}
// DRAWER MODE — custom element triggers init when connected to the DOM
class GiftCardDrawer extends DrawerComponent {
  constructor() {
    super();
    initGiftCardBalanceChecker();
  }
}
if (!customElements.get('giftcard-drawer')) {
customElements.define('giftcard-drawer', GiftCardDrawer);
}

// PAGE MODE — init on DOMContentLoaded (no custom element present)
document.addEventListener('DOMContentLoaded', () => {
const sectionEl = document.querySelector('[data-display-mode]');
if (sectionEl?.dataset.displayMode === 'page') {
initGiftCardBalanceChecker();
}
});